/**
 * "Client portal" guided tour -- the staff Portal panel on a deal (D9 WP24,
 * #3551; the panel itself is D9-POR-010 / D9-POR-015, the partner panel
 * D9-EXT-009).
 *
 * LAUNCHER-ONLY, like the test-deal practice tour: relaunchable from the
 * Training Guided Tours panel (grouped under "Client portal") and deep-linkable
 * via ``?tour=portal-deal-panel``. It is never a first-visit nudge
 * (sectionNudges.ts) and is not route-wired -- the ``/crm/deals`` launcher slot
 * stays the deals section tour and ``/crm/deals/:id`` stays the deal-file tour.
 *
 * WHY A HAND-OFF: the Portal panel lives on a single deal, and a tour started
 * from Training has no deal id to navigate to (issue #2688 -- a ``/:id``-first
 * tour may never be listed in Training). So the tour opens on the static deals
 * list and its second step is a HAND-OFF step (``advanceOnRoute``): the user
 * opens any deal themselves and the engine carries on inside it. Every later
 * step uses the ``/crm/deals/:id`` pattern route, which the concrete path then
 * matches, so the engine stays on that deal.
 *
 * The first deal step opens the Buyers / Sellers tab via ``openVia`` -- a tab
 * switch is a pure UI toggle (it submits and mutates nothing), which is the
 * ``openVia`` safety contract. The tour never opens the Send portal dialog or
 * clicks a send / revoke control: it only points at them.
 *
 * AUDIENCE: the ``client_portal`` module (D9-WLB-007) -- with the module off the
 * panel never renders, so the tour is not offered -- and, like the agent deal
 * tours, withheld from the single-surface back-office presets
 * (``BACK_OFFICE_PRESETS``): their deal work is on their own desk / the file
 * cover page, not the ``/crm/deals`` list this tour starts on (the Office Admin
 * has no Deals menu at all). Agents and admins get it. The panel is also hidden
 * on a deal the user does not own (the server answers 404), so the hand-off
 * copy asks for one of the user's OWN deals.
 *
 * Copy rules (D4-ONB-011 / D4-WLB): agency-neutral via ``{agency}``, plain
 * language for realtors, no emojis.
 */
import type { TourDefinition } from "./types";
import { BACK_OFFICE_PRESETS } from "./agentSectionTours";

/** The deal-detail pattern route every in-deal step lives on. */
export const DEAL_DETAIL_ROUTE = "/crm/deals/:id";

export const PORTAL_PANEL_TOUR: TourDefinition = {
  id: "portal-deal-panel",
  title: "Client portal tour",
  audience: { module: "client_portal", excludePreset: BACK_OFFICE_PRESETS },
  steps: [
    {
      id: "intro",
      route: "/crm/deals",
      target: "center",
      section: "Client portal",
      title: "Keep your clients in the loop",
      body: "The client portal is a private page for each buyer and seller on a deal. They follow the deal's progress, see what is still needed from them and send documents -- all from a link you send them, with no password to remember. This tour shows you where to send it from and how to follow up. You can leave any time with End tour or the Esc key.",
      placement: "center",
    },
    {
      // HAND-OFF step: waits for the user to open a deal, then advances.
      id: "open-a-deal",
      route: "/crm/deals",
      target: "page-deals",
      advanceOnRoute: DEAL_DETAIL_ROUTE,
      section: "Open a deal",
      title: "Open one of your deals",
      body: "The portal is sent from inside a deal. Open one of your own existing deals from the list below (not Add Sale or Add Lease) -- the tour carries on as soon as the deal opens. No deals yet? Choose End tour and come back once you have one.",
      placement: "bottom",
    },
    {
      id: "panel",
      route: DEAL_DETAIL_ROUTE,
      target: "deal-portal-panel",
      openVia: "deal-tab-parties",
      section: "The Client portal panel",
      title: "The Client portal panel",
      body: "On the Buyers / Sellers tab, the Client portal panel lists every buyer and seller on this deal, with where their portal link stands.",
      placement: "bottom",
    },
    {
      id: "status",
      route: DEAL_DETAIL_ROUTE,
      target: "deal-portal-parties",
      section: "Link status",
      title: "Follow each client at a glance",
      body: "Each person shows a status: Not sent, Sent, Opened, ID check passed, Revoked or Expired. You also see when they last opened their portal, how many documents they have uploaded and how many still wait for review. The deal's agent and paralegal are notified when a client first opens their link or uploads a document.",
      placement: "top",
    },
    {
      id: "send",
      route: DEAL_DETAIL_ROUTE,
      target: "deal-portal-header",
      section: "Send portal",
      title: "Send the portal",
      body: "Send portal (top right of the panel, or Send on a single person) walks you through it: confirm the attorneys and bond originator the client will see, pick the buyer or seller, choose how to send -- copy the link or WhatsApp it from your phone, plus WhatsApp or email when {agency} has those switched on -- check the message, then tick to confirm the name, property and cell or email it is going to. The link is shown to you once, so send it straight away.",
      placement: "bottom",
    },
    {
      id: "resend-revoke",
      route: DEAL_DETAIL_ROUTE,
      target: "deal-portal-parties",
      section: "Re-send and revoke",
      title: "Re-send or revoke a link",
      body: "Re-send gives the client a fresh link and the old one stops working. Revoke switches a link off straight away, with a reason. Links also switch off by themselves when the deal is cancelled, a party is replaced or their cell or email is changed on the deal. Unless the check is switched off for the deal, before a client sees any figures or uploads a document they confirm the last four digits of their ID or cell number.",
      placement: "top",
    },
    {
      id: "partners",
      route: DEAL_DETAIL_ROUTE,
      target: "deal-shares-panel",
      section: "Partner sharing",
      title: "Share the deal with attorneys and originators",
      body: "Below it, Partner sharing lets the transferring attorney, the bond attorney or a bond originator follow this deal through their own secure link. Sharing is never automatic -- you confirm each firm, and you can stop sharing at any time.",
      placement: "top",
    },
    {
      id: "finish",
      route: DEAL_DETAIL_ROUTE,
      target: "center",
      section: "Finish",
      title: "That is the client portal",
      body: "Send the portal once the buyer or seller is captured on the deal, then watch the statuses here. You can retake this tour any time from Training.",
      placement: "center",
    },
  ],
};
